import _ from "lodash";
import { Map } from "immutable";
import { Big } from "big.js";

import ImmutableUtils from "./ImmutableUtils";
import PropertyMaps from "../constants/PropertyMaps";
import AppConstants from "../constants/AppConstants";
import BlockUpdateV0 from "../vo/BlockUpdate.mjs";
import { BUTTON } from "@/constants/DataTypes";
import { gatherProps } from "./Merge/MergeUtils.mjs";
import {
  BUTTON_BORDER_WIDTH,
  BUTTON_PADDING,
  BUTTON_TEXT_LINE_HEIGHT,
  BUTTON_TEXT_SIZE,
} from "@/constants/MergeConstants";
import { getPropUpdateDetails } from "./PropUpdateUtils";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE } from "@/constants/DesignConstants.mjs";
import BlockUtils from "@/utils/BlockUtils";

const TEXT_FRIENDLY_NAME_SELECTORS = [
  ["name"],
  ["content", "sample"],
  ["type"],
];
const DEFAULT_FRIENDLY_NAME_SELECTORS = [
  ["name"],
  ["slug"],
  ["style"],
  ["type"],
];
const MIN_CHARACTER_WIDTH = 10;
const BUTTON_HEIGHT_PROPS = ["bW", "text.lH", "text.size", "padding"];
const BUTTON_WIDTH_PROPS = ["bW", "text.size", "padding"];

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS[number]} Layouts
 *
 */

class ElementUtils {
  constructor() {}

  applyTextStyle = (layout, elements, newTextStyle, providedBlockUpdator) => {
    const blockUpdator = providedBlockUpdator
      ? providedBlockUpdator
      : new BlockUpdateV0();
    this.resetTextProps(layout, elements, blockUpdator);

    const elementArray = Array.isArray(elements) ? elements : [elements];
    elementArray.forEach((element) => {
      const updateName = layout + ".style";

      const updatePayload = {
        name: updateName,
        value: newTextStyle,
      };

      blockUpdator.addPropUpdate(
        element.getIn(["selected", "id"]),
        updatePayload,
      );
    });

    return blockUpdator;
  };

  applyTextStyleById = (layout, ids, blockUpdator, newTextStyle) => {
    const IdArray = Array.isArray(ids) ? ids : [ids];
    IdArray.forEach((id) => {
      const updateName = layout + ".style";

      const updatePayload = {
        name: updateName,
        value: newTextStyle,
      };

      blockUpdator.addPropUpdate(id, updatePayload);
    });
  };

  applyBothTextStyles = (elements, blockUpdator, newTextStyle) => {
    this.applyTextStyle(LAYOUT_DESKTOP, elements, newTextStyle, blockUpdator);
    this.applyTextStyle(LAYOUT_MOBILE, elements, newTextStyle, blockUpdator);
  };

  applyTextProperties = (layout, elements, blockUpdator, newTextProperties) => {
    const elementArray = Array.isArray(elements) ? elements : [elements];
    elementArray.forEach((element) => {
      Object.keys(newTextProperties.toJS()).forEach((propertyName) => {
        const updateName = layout + "." + propertyName;
        const updatePayload = {
          name: updateName,
          value: newTextProperties.get(propertyName),
        };

        blockUpdator.addPropUpdate(
          element.getIn(["selected", "id"]),
          updatePayload,
        );
      });
    });
  };
  applyTextPropertiesById = (layout, ids, blockUpdator, newTextProperties) => {
    const IdArray = Array.isArray(ids) ? ids : [ids];
    IdArray.forEach((id) => {
      Object.keys(newTextProperties.toJS()).forEach((propertyName) => {
        const updateName = layout + "." + propertyName;
        const updatePayload = {
          name: updateName,
          value: newTextProperties.get(propertyName),
        };

        blockUpdator.addPropUpdate(id, updatePayload);
      });
    });
  };

  applyBothTextProperties = (elements, blockUpdator, newTextProperties) => {
    this.applyTextProperties(
      LAYOUT_DESKTOP,
      elements,
      blockUpdator,
      newTextProperties,
    );
    this.applyTextProperties(
      LAYOUT_MOBILE,
      elements,
      blockUpdator,
      newTextProperties,
    );
  };

  containsTextElement = (elements) => {
    return elements.map((element) => element.type).includes("text");
  };

  getFriendlyElementName = (element) => {
    let friendlyNameSelectors = DEFAULT_FRIENDLY_NAME_SELECTORS;
    let friendlyName = null;

    switch (element.get("type")) {
      case AppConstants.ELEMENT_TYPE_TEXT:
        friendlyNameSelectors = TEXT_FRIENDLY_NAME_SELECTORS;
        break;
    }

    for (
      let i = 0;
      i < friendlyNameSelectors.length && friendlyName === null;
      i++
    ) {
      friendlyName = element.getIn(friendlyNameSelectors[i], null);
    }

    return friendlyName;
  };

  getSelectedElementIds = (block) => {
    const selectedElements = block.getIn(["selected", "elements"]);
    // Can't have a selected element be empty or undefined
    if (!selectedElements) {
      console.warn("Something is up with trying to get selected elements");
      return [];
    }
    return selectedElements
      .keySeq()
      .toArray()
      .reduce((ids, id) => {
        const element = BlockUtils.getBlockElement(block, id);
        if (!!element) {
          ids.push(id);
        }
        return ids;
      }, []);
  };

  getSelectedElements = (block) =>
    this.getSelectedElementIds(block).map((id) =>
      BlockUtils.getBlockElement(block, id),
    );

  /**
   * Calculates the minimum height required for a JavaScript element based on its type and properties.
   * @param {Object} element - The element object containing type and layout-specific properties.
   * @param {Layouts} layout - The layout context (desktop/mobile).
   * @param {Object} theme - The theme object containing styling information.
   * @returns {number} The minimum height in pixels required for the element.
   */
  getMinHeightJS = (element, layout, theme) => {
    const { type } = element;
    const processes = {
      [BUTTON]: () => {
        const props = gatherProps(
          element,
          layout,
          BUTTON_HEIGHT_PROPS,
          false,
          theme,
        );

        const lineHeight = (props?.text?.size ?? 16) * (props?.text?.lH ?? 1);
        const [top, _, bottom, __] = props.padding
          .split(" ")
          .map((val) => parseInt(val, 10) || 0);

        return new Big(lineHeight ?? 0)
          .plus(top)
          .plus(bottom)
          .plus(new Big(props?.bW ?? 0).times(2))
          .toNumber();
      },
    };

    return Math.max(type in processes ? processes[type]() : 1, 1);
  };

  /**
   * Calculates the minimum height required for an Immutable.js element based on its type and properties.
   * @param {import("immutable").Map} element - The immutable element containing type and layout-specific properties.
   * @param {Layouts} layout - The layout context (desktop/mobile).
   * @param {Object} theme - The theme object containing styling information.
   * @returns {number} The minimum height in pixels required for the element.
   */
  getMinHeightImmutable = (element, layout, theme) => {
    const type = element.get("type");
    const processes = {
      [BUTTON]: () => {
        const props = gatherProps(
          element,
          layout,
          BUTTON_HEIGHT_PROPS,
          false,
          theme,
        );

        const lineHeight =
          props.getIn(BUTTON_TEXT_SIZE.split("."), 16) *
          props.getIn(BUTTON_TEXT_LINE_HEIGHT.split("."), 1);
        const [top, _, bottom, __] = props
          .get(BUTTON_PADDING)
          .split(" ")
          .map((val) => parseInt(val, 10) || 0);

        return Big(lineHeight)
          .plus(top)
          .plus(bottom)
          .plus(new Big(props.get(BUTTON_BORDER_WIDTH, 0)).times(2))
          .toNumber();
      },
    };

    return Math.max(type in processes ? processes[type]() : 1, 1);
  };

  /**
   * Calculates the minimum height required for an element, automatically choosing between JavaScript and Immutable.js implementations.
   * @param {Object|import("immutable").Map} element - The element object (either plain JS or Immutable.js Map).
   * @param {Layouts} layout - The layout context (desktop/mobile).
   * @param {Object} theme - The theme object containing styling information.
   * @returns {number} The minimum height in pixels required for the element.
   */
  getMinHeight = (element, layout, theme) => {
    return Map.isMap(element)
      ? this.getMinHeightImmutable(element, layout, theme)
      : this.getMinHeightJS(element, layout, theme);
  };

  /**
   * Calculates the minimum width required for a JavaScript element based on its type and properties.
   * @param {Object} element - The element object containing type and layout-specific properties.
   * @param {Layouts} layout - The layout context (desktop/mobile).
   * @param {Object} theme - The theme object containing styling information.
   * @returns {number} The minimum width in pixels required for the element.
   */
  getMinWidthJS = (element, layout, theme) => {
    const { type } = element;
    const processes = {
      [BUTTON]: () => {
        const props = gatherProps(
          element,
          layout,
          BUTTON_WIDTH_PROPS,
          false,
          theme,
        );

        const [_, right, __, left] = props.padding
          .split(" ")
          .map((val) => parseInt(val, 10) || 0);

        return new Big(props?.text?.size ?? 16)
          .plus(right)
          .plus(left)
          .plus(new Big(props.bW ?? 0).times(2))
          .plus(MIN_CHARACTER_WIDTH)
          .toNumber();
      },
    };

    return Math.max(type in processes ? processes[type]() : 1, 1);
  };

  /**
   * Calculates the minimum width required for an Immutable.js element based on its type and properties.
   * @param {import("immutable").Map} element - The immutable element containing type and layout-specific properties.
   * @param {Layouts} layout - The layout context (desktop/mobile).
   * @param {Object} theme - The theme object containing styling information.
   * @returns {number} The minimum width in pixels required for the element.
   */
  getMinWidthImmutable = (element, layout, theme) => {
    const type = element.get("type");
    const processes = {
      [BUTTON]: () => {
        const props = gatherProps(
          element,
          layout,
          BUTTON_WIDTH_PROPS,
          false,
          theme,
        );

        const textSize = props.getIn(BUTTON_TEXT_SIZE.split("."), 16);
        const [_, right, __, left] = props
          .get(BUTTON_PADDING)
          .split(" ")
          .map((val) => parseInt(val, 10) || 0);

        return new Big(textSize)
          .plus(right)
          .plus(left)
          .plus(new Big(props.get(BUTTON_BORDER_WIDTH, 0)))
          .plus(MIN_CHARACTER_WIDTH)
          .toNumber();
      },
    };

    return Math.max(type in processes ? processes[type]() : 1, 1);
  };

  /**
   * Calculates the minimum width required for an element, automatically choosing between JavaScript and Immutable.js implementations.
   * @param {Object|import("immutable").Map} element - The element object (either plain JS or Immutable.js Map).
   * @param {Layouts} layout - The layout context (desktop/mobile).
   * @param {Object} theme - The theme object containing styling information.
   * @returns {number} The minimum width in pixels required for the element.
   */
  getMinWidth = (element, layout, theme) => {
    return Map.isMap(element)
      ? this.getMinWidthImmutable(element, layout, theme)
      : this.getMinWidthJS(element, layout, theme);
  };

  /**
   * Adjusts element size to meet minimum requirements based on content, padding, and borders.
   * Ensures elements like buttons maintain proper dimensions to fit their content.
   * @param {import("immutable").Map} element - The immutable element to adjust.
   * @param {Object} theme - The theme object containing styling information.
   * @param {import("@/vo/BlockUpdate.mjs").PropUpdate} propInfo - The property update information.
   * @returns {Array<import("@/vo/BlockUpdate.mjs").PropUpdate>} Array of property updates needed to meet minimum size requirements.
   */
  adjustSizeToMinIfNeeded = (element, theme, propInfo) => {
    const type = element.get("type");
    const updates = [propInfo];
    if (type === BUTTON) {
      const { value } = propInfo;
      const [name] = getPropUpdateDetails(propInfo);
      const activeLayout = element.getIn(["selected", "layout"]);
      const isWidthUpdate = name === "w";
      const isHeightUpdate = name === "h";

      const preparedElement = element.setIn(propInfo.name.split("."), value);

      const width = element.getIn([activeLayout, "w"], 0);
      const height = element.getIn([activeLayout, "h"], 0);

      // Tracks the minimum size of the element to fit text/padding/border
      const minHeight = this.getMinHeight(preparedElement, activeLayout, theme);
      const minWidth = this.getMinWidth(preparedElement, activeLayout, theme);

      if (value < minWidth && isWidthUpdate) {
        const widthUpdate = updates.find(
          (propUpdate) => propUpdate.name === propInfo.name,
        );
        widthUpdate.value = minWidth;
      } else if (width < minWidth && !isWidthUpdate) {
        updates.push({
          name: `${activeLayout}.w`,
          value: minWidth,
          addUndo: false,
          source: "propertyInput",
        });
      }

      if (value < minHeight && isHeightUpdate) {
        const heightUpdate = updates.find(
          (propUpdate) => propUpdate.name === propInfo.name,
        );
        heightUpdate.value = minHeight;
      } else if (height < minHeight && !isHeightUpdate) {
        updates.push({
          name: `${activeLayout}.h`,
          value: minHeight,
          addUndo: false,
          source: "propertyInput",
        });
      }
    }

    return updates;
  };

  getElement = (target, sourceElements) => {
    return sourceElements.find(
      (e) => e.getIn(["selected", "id"]) === target.getIn(["selected", "id"]),
    );
  };

  getElementById = (id, block) => {
    let accessor = id.split(".").slice(1);
    return block.getIn(accessor);
  };

  getElementIds = (elements) => {
    const elementArray = Array.isArray(elements) ? elements : [elements];

    return elementArray.map((element) => {
      return element.getIn(["selected", "id"]);
    });
  };

  getTextProperties = (layout, element) => {
    return element
      .get(layout)
      .filter(ImmutableUtils.keyIn(PropertyMaps.TEXT_PROPS));
  };

  hasPNG = (content) => {
    const fileName = content ? content.get("key", false) : false;
    return fileName && fileName.endsWith(".png");
  };

  isSimpleGallery = (elementType, content) => {
    const galleryType = content ? content.get("type", "simple") : "";
    const isGallery = elementType === AppConstants.ELEMENT_TYPE_GALLERY;
    return isGallery && galleryType === "simple";
  };

  isTextElementById = (id, block) => {
    return this.getElementById(id, block).get("type") === "text";
  };

  matchTextFormat = (layout, targets, source, providedBlockUpdator) => {
    const blockUpdator = providedBlockUpdator
      ? providedBlockUpdator
      : new BlockUpdateV0();
    const targetArray = Array.isArray(targets) ? targets : [targets];

    this.applyTextStyle(
      layout,
      targetArray,
      source.getIn([layout, "style"]),
      blockUpdator,
    );
    this.applyTextProperties(
      layout,
      targetArray,
      blockUpdator,
      this.getTextProperties(layout, source),
    );

    return blockUpdator;
  };

  resetBothTextProps = (elements, blockUpdator) => {
    this.resetTextProps(LAYOUT_DESKTOP, elements, blockUpdator);
    this.resetTextProps(LAYOUT_MOBILE, elements, blockUpdator);
  };

  resetTextProps = (layout, elements, blockUpdator) => {
    const elementArray = Array.isArray(elements) ? elements : [elements];
    elementArray.forEach((element) => {
      const elemProps = element.get(layout);
      const textProps = elemProps.filter(
        ImmutableUtils.keyIn(PropertyMaps.TEXT_PROPS),
      );

      // resets text properties
      textProps.forEach((value, prop) => {
        blockUpdator.add(
          element.getIn(["selected", "id"]),
          [layout, prop],
          null,
        );
      });

      blockUpdator.add(
        element.getIn(["selected", "id"]),
        [layout, "styles"],
        null,
      );
    });
  };
}

export const offsetElement = (element, offset = 16) => {
  return element
    .setIn(["mobile", "x"], element.getIn(["mobile", "x"], 0) + offset)
    .setIn(["mobile", "y"], element.getIn(["mobile", "y"], 0) + offset)
    .setIn(["desktop", "x"], element.getIn(["desktop", "x"], 0) + offset)
    .setIn(["desktop", "y"], element.getIn(["desktop", "y"], 0) + offset);
};

export default new ElementUtils();
